import { unwrapApiPayload } from '@/utils/graphql'

function pickString(source, ...keys) {
  if (!source || typeof source !== 'object') return ''
  for (const key of keys) {
    const value = String(source[key] ?? '').trim()
    if (value) return value
  }
  return ''
}

export function pickOrgApiKey(org) {
  // gateway_api_key is this org's OWN API Gateway usage-plan key -- the value
  // the public API actually checks, and the one that gives each customer their
  // own throttle and their own off-switch.
  //
  // `api_key` is deliberately NOT consulted: that is a legacy org-lookup
  // fallback inside the Lambda which API Gateway rejects with a bare 403, and
  // showing it here as "API key" is what sent an integrator into 139 straight
  // failures on 2026-08-11.
  return pickString(org, 'gateway_api_key', 'gatewayApiKey')
}

export function pickOrgOAuthClient(org) {
  const clientId = pickString(
    org,
    'oauth_client_id',
    'client_id',
    'oauthClientId',
    'clientId',
  )
  const clientSecret = pickString(
    org,
    'oauth_client_secret',
    'client_secret',
    'oauthClientSecret',
    'clientSecret',
  )
  const tokenUrl = pickString(
    org,
    'oauth_token_url',
    'token_url',
    'oauthTokenUrl',
    'tokenUrl',
  )

  return {
    clientId,
    clientSecret,
    tokenUrl,
    hasClient: Boolean(clientId || clientSecret),
  }
}

export function extractGeneratedApiKey(response) {
  const payload = unwrapApiPayload(response) ?? response
  const data =
    payload?.orgDetails ??
    payload?.items?.orgDetails ??
    payload?.data?.items?.orgDetails ??
    payload

  return pickString(data, 'api_key', 'apiKey', 'key')
}

export function extractGeneratedOAuthClient(response) {
  const payload = unwrapApiPayload(response) ?? response
  const data =
    payload?.orgDetails ??
    payload?.items?.orgDetails ??
    payload?.data?.items?.orgDetails ??
    payload

  return {
    clientId: pickString(data, 'oauth_client_id', 'client_id', 'clientId'),
    clientSecret: pickString(
      data,
      'oauth_client_secret',
      'client_secret',
      'clientSecret',
    ),
    tokenUrl: pickString(data, 'oauth_token_url', 'token_url', 'tokenUrl'),
  }
}

export function mergeOrgCredentialUpdate(response, organization = {}) {
  const apiKey = extractGeneratedApiKey(response)
  const oauth = extractGeneratedOAuthClient(response)

  const next = { ...organization }
  // gateway_api_key is what the card reads. Writing only api_key here is why
  // Regenerate appeared to do nothing: the backend issued a new key, the store
  // kept the old field, and the card fell back to the shared environment key.
  if (apiKey) {
    next.gateway_api_key = apiKey
    next.api_key = apiKey
  }
  if (oauth.clientId) {
    next.oauth_client_id = oauth.clientId
    next.client_id = oauth.clientId
  }
  if (oauth.tokenUrl) {
    next.oauth_token_url = oauth.tokenUrl
    next.token_url = oauth.tokenUrl
  }
  if (oauth.clientSecret) {
    next.oauth_client_secret = oauth.clientSecret
    next.client_secret = oauth.clientSecret
  }

  return next
}
